import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { database } from '../Firebase';

const CATEGORIES = [
  { key: 'bug',        label: '🐛 Bug',        color: '#dc3545', bg: '#fce8e8' },
  { key: 'suggestion', label: '💡 Suggestion',  color: '#0d6efd', bg: '#e8f0fe' },
  { key: 'compliment', label: '❤️ Compliment',  color: '#198754', bg: '#d1e7dd' },
  { key: 'autre',      label: '💬 Autre',       color: '#6c757d', bg: '#f8f9fa' },
];

// ── Étoiles ───────────────────────────────────────────────────────────────────
const EtoilesInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '12px 0' }}>
      {[1, 2, 3, 4, 5].map(e => (
        <span key={e}
          onClick={() => onChange(e)}
          onMouseEnter={() => setHover(e)}
          onMouseLeave={() => setHover(0)}
          style={{
            fontSize: 'clamp(1.8rem, 8vw, 2.4rem)',
            cursor: 'pointer',
            color: e <= (hover || value) ? '#ffc107' : '#dee2e6',
            transition: 'color 0.1s ease', userSelect: 'none',
            // Zone de tap confortable sur mobile
            padding: '4px',
            minWidth: '44px', minHeight: '44px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          ★
        </span>
      ))}
    </div>
  );
};

// ── Validation ────────────────────────────────────────────────────────────────
const validateDescription = (text) => {
  const trimmedText = text?.trim() || '';
  if (!trimmedText)                     return { isValid: false, error: "La description est requise." };
  if (trimmedText.length < 10)          return { isValid: false, error: `Minimum 10 caractères (${trimmedText.length}/10).` };
  if (trimmedText.length > 500)         return { isValid: false, error: "Maximum 500 caractères autorisés." };
  if (/[<>{}[\]\\]/g.test(trimmedText)) return { isValid: false, error: "Caractères non autorisés (< > { } [ ] \\)." };
  return { isValid: true, error: null };
};

// ── Compteur caractères ───────────────────────────────────────────────────────
const SecureCharacterCounter = ({ length, maxLength = 500 }) => {
  const isOverLimit = length > maxLength;
  const isNearLimit = length > maxLength - 50 && length <= maxLength;
  const remaining   = maxLength - length;
  const statusClass = isOverLimit ? 'text-danger' : isNearLimit ? 'text-warning' : 'text-muted';
  return (
    <div>
      <Form.Text className={statusClass} style={{ fontSize: '0.78rem' }}>
        {length}/{maxLength} caractères
      </Form.Text>
      {isNearLimit && !isOverLimit && (
        <Form.Text className="text-warning d-block" style={{ fontSize: '0.75rem' }}>
          ⚠️ Plus que {remaining} caractères restants.
        </Form.Text>
      )}
      {isOverLimit && (
        <div className="text-danger" style={{ fontSize: '0.75rem', fontWeight: 500, marginTop: '2px' }}>
          ⚠️ {Math.abs(remaining)} caractères en trop.
        </div>
      )}
    </div>
  );
};

export default function Suggestions() {
  const [userData, setUserData]             = useState(null);
  const [loading, setLoading]               = useState(true);
  const [submitting, setSubmitting]         = useState(false);
  const [success, setSuccess]               = useState(false);
  const [error, setError]                   = useState('');
  const [mesSuggestions, setMesSuggestions] = useState([]);
  const [activeTab, setActiveTab]           = useState('nouveau');
  const [note, setNote]                     = useState(0);
  const [categorie, setCategorie]           = useState('');
  const [description, setDescription]       = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const auth      = getAuth();
  const firestore = getFirestore();

  useEffect(() => {
    const init = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const userSnap = await getDoc(doc(firestore, 'users', user.uid));
        if (userSnap.exists()) setUserData(userSnap.data());
        await chargerMesSuggestions(user.uid);
      } catch (err) {
        console.error("Erreur init suggestions:", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line

  const chargerMesSuggestions = async (userId) => {
    try {
      const q    = query(
        collection(database, 'suggestions'),
        where('userId', '==', userId),
        orderBy('dateCreation', 'desc')
      );
      const snap = await getDocs(q);
      setMesSuggestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn("Erreur chargement suggestions:", err);
    }
  };

  const handleDescriptionChange = (e) => {
    let value = e.target.value;
    if (value.length > 500) value = value.slice(0, 500);
    setDescription(value);
    const validation = validateDescription(value);
    setDescriptionError(validation.isValid ? '' : validation.error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categorie)  return setError("Veuillez choisir une catégorie.");
    if (note === 0)  return setError("Veuillez donner une note globale.");
    const descValidation = validateDescription(description);
    if (!descValidation.isValid) {
      setDescriptionError(descValidation.error);
      return setError(descValidation.error);
    }

    setSubmitting(true);
    setError('');
    setDescriptionError('');

    try {
      const user = auth.currentUser;
      if (!user) { setError("Utilisateur non authentifié."); return; }

      const cleanDescription = description.trim().replace(/[<>]/g, '').slice(0, 500);

      await addDoc(collection(database, 'suggestions'), {
        userId:         user.uid,
        pseudo:         userData?.pseudo || 'Anonyme',
        estPionnier:    userData?.estPionnier || false,
        secteur:        userData?.secteurPionnier || null,
        numeroPionnier: userData?.numeroPionnier || null,
        note, categorie,
        description: cleanDescription,
        statut:      'nouveau',
        dateCreation: serverTimestamp(),
      });

      setSuccess(true);
      setNote(0);
      setCategorie('');
      setDescription('');
      setDescriptionError('');
      await chargerMesSuggestions(user.uid);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      console.error("Erreur Firestore:", err);
      setError("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatutBadge = (statut) => {
    const config = {
      nouveau: { label: 'Nouveau',   bg: '#ffc107', color: '#000' },
      lu:      { label: 'Lu ✓',      bg: '#0d6efd', color: '#fff' },
      traité:  { label: 'Traité ✅', bg: '#198754', color: '#fff' },
    };
    const c = config[statut] || config.nouveau;
    return (
      <span style={{
        fontSize: '0.7rem', background: c.bg, color: c.color,
        padding: '2px 8px', borderRadius: '10px', fontWeight: 600,
      }}>
        {c.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: '#07a993' }} />
      </Container>
    );
  }

  return (
    <Container className="py-4 px-3" style={{ maxWidth: '600px' }}>

      {/* ── En-tête ────────────────────────────────────────────────────────── */}
      <div className="text-center mb-4">
        <div style={{ fontSize: '2.5rem' }}>💡</div>
        <h4 style={{ fontWeight: 700, fontSize: 'clamp(1.1rem, 4vw, 1.4rem)' }}>
          Vos suggestions
        </h4>
        {userData?.estPionnier && (
          <Badge style={{
            background: 'linear-gradient(135deg, #07a993, #0d6efd)',
            fontSize: '0.78rem', padding: '6px 12px',
          }}>
            🏅 Pionnier #{userData.numeroPionnier} — {userData.secteurPionnier}
          </Badge>
        )}
        <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
          Votre avis nous aide à améliorer SpotyPark.
          {userData?.estPionnier && " En tant que pionnier, votre retour est particulièrement précieux !"}
        </p>
      </div>

      {/* ── Onglets ────────────────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-4">
        {[
          { key: 'nouveau',    label: '✏️ Nouveau' },
          { key: 'historique', label: `📋 Historique (${mesSuggestions.length})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: '8px',
              border: `2px solid ${activeTab === key ? '#07a993' : '#dee2e6'}`,
              background: activeTab === key ? '#e8f8f5' : '#fff',
              fontWeight: activeTab === key ? 700 : 400,
              cursor: 'pointer',
              fontSize: 'clamp(0.82rem, 3vw, 0.95rem)',
              transition: 'all 0.15s ease',
              minHeight: '44px',
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Formulaire nouvelle suggestion ────────────────────────────────── */}
      {activeTab === 'nouveau' && (
        <>
          {success && (
            <Alert variant="success" style={{ borderLeft: '4px solid #198754', fontSize: '0.88rem' }}>
              ✅ Merci ! Votre suggestion a bien été envoyée.
            </Alert>
          )}
          {error && (
            <Alert variant="danger" style={{ fontSize: '0.85rem' }}>{error}</Alert>
          )}

          <Form onSubmit={handleSubmit}>

            {/* Note globale */}
            <div className="p-3 rounded mb-3" style={{
              background: '#f8f9fa', border: '1px solid #dee2e6', textAlign: 'center',
            }}>
              <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '0.92rem' }}>
                Note globale de l'application
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '4px' }}>
                Quelle note donnez-vous à SpotyPark ?
              </div>
              <EtoilesInput value={note} onChange={setNote} />
              {note > 0 && (
                <div style={{ fontSize: '0.85rem', color: '#ffc107', fontWeight: 600 }}>
                  {['', 'Très mauvais 😞', 'Mauvais 😕', 'Correct 😐', 'Bien 😊', 'Excellent 🤩'][note]}
                </div>
              )}
            </div>

            {/* Catégorie — radio cards responsive */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 700, fontSize: '0.92rem' }}>Catégorie</Form.Label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)', // 2 colonnes sur mobile
                gap: '8px',
              }}>
                {CATEGORIES.map(cat => (
                  <label key={cat.key} style={{
                    padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                    border: `2px solid ${categorie === cat.key ? cat.color : '#dee2e6'}`,
                    background: categorie === cat.key ? cat.bg : '#fff',
                    fontWeight: categorie === cat.key ? 700 : 400,
                    fontSize: '0.85rem', transition: 'all 0.15s ease',
                    textAlign: 'center', minHeight: '44px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <input type="radio" name="categorie" value={cat.key}
                      checked={categorie === cat.key}
                      onChange={() => setCategorie(cat.key)}
                      style={{ display: 'none' }} />
                    {cat.label}
                  </label>
                ))}
              </div>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                {categorie === 'bug'        ? '🐛 Décrivez le bug rencontré' :
                 categorie === 'suggestion' ? '💡 Décrivez votre idée'        :
                 categorie === 'compliment' ? "❤️ Qu'avez-vous aimé ?"        :
                 '💬 Votre message'}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={
                  categorie === 'bug'
                    ? "Ex: Quand je clique sur Réserver, l'app se bloque sur..."
                    : categorie === 'suggestion'
                    ? "Ex: Ce serait bien de pouvoir filtrer les places par..."
                    : categorie === 'compliment'
                    ? "Ex: J'adore la fonctionnalité de signalement..."
                    : "Décrivez votre message..."
                }
                maxLength={500}
                style={{
                  resize: 'none',
                  fontSize: '16px', // évite zoom iOS
                  borderColor: descriptionError ? '#dc3545' : undefined,
                }}
                isInvalid={!!descriptionError}
              />
              <SecureCharacterCounter length={description.length} maxLength={500} />
              {descriptionError && (
                <Form.Text className="text-danger d-block mt-1" style={{ fontSize: '0.78rem' }}>
                  ❌ {descriptionError}
                </Form.Text>
              )}
              {description.length > 0 && description.length < 50 && !descriptionError && (
                <Form.Text className="text-info d-block mt-1" style={{ fontSize: '0.78rem' }}>
                  💡 N'hésitez pas à détailler votre message (minimum 10 caractères)
                </Form.Text>
              )}
            </Form.Group>

            <Button type="submit"
              disabled={submitting || !!descriptionError}
              className="w-100"
              style={{
                background: 'linear-gradient(135deg, #07a993, #0d6efd)',
                border: 'none', fontWeight: 700, padding: '14px',
                fontSize: '0.95rem', borderRadius: '10px',
                opacity: submitting || !!descriptionError ? 0.7 : 1,
              }}>
              {submitting ? 'Envoi en cours...' : '💡 Envoyer ma suggestion'}
            </Button>
          </Form>
        </>
      )}

      {/* ── Historique ────────────────────────────────────────────────────── */}
      {activeTab === 'historique' && (
        <>
          {mesSuggestions.length === 0 ? (
            <div className="text-center py-5" style={{
              border: '2px dashed #dee2e6', borderRadius: '12px', background: '#f8f9fa',
            }}>
              <div style={{ fontSize: '2.5rem' }}>💡</div>
              <p className="mt-2 text-muted" style={{ fontSize: '0.88rem' }}>
                Vous n'avez pas encore envoyé de suggestion.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {mesSuggestions.map(s => {
                const cat = CATEGORIES.find(c => c.key === s.categorie) || CATEGORIES[3];
                return (
                  <div key={s.id} className="p-3 rounded" style={{
                    border:     `1px solid ${cat.color}44`,
                    background: cat.bg,
                    borderLeft: `4px solid ${cat.color}`,
                  }}>
                    {/* Header card */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginBottom: '8px',
                      flexWrap: 'wrap', gap: '4px',
                    }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                          {cat.label}
                        </span>
                        {getStatutBadge(s.statut)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#6c757d', flexShrink: 0 }}>
                        {formatDate(s.dateCreation)}
                      </div>
                    </div>

                    {/* Note */}
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ color: '#ffc107', fontSize: '0.88rem' }}>
                        {'★'.repeat(s.note)}{'☆'.repeat(5 - s.note)}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#6c757d', marginLeft: '6px' }}>
                        {s.note}/5
                      </span>
                    </div>

                    {/* Description */}
                    <p style={{ fontSize: '0.85rem', color: '#333', marginBottom: 0, lineHeight: 1.5 }}>
                      {s.description}
                    </p>

                    {/* Réponse admin */}
                    {s.reponseAdmin && (
                      <div className="mt-2 p-2 rounded" style={{
                        background: '#e8f8f5', border: '1px solid #07a993',
                        fontSize: '0.82rem',
                      }}>
                        <strong style={{ color: '#07a993' }}>💬 Réponse SpotyPark :</strong>
                        <p className="mb-0 mt-1">{s.reponseAdmin}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Container>
  );
}